import { SupportingDocumentGridOption } from '@app/pages/page-designer/supporting-document-grid/supporting-document-grid-config.model';

/** Education tab `profile_tab_section` values that store grid-string category/type. */
export const PROFILE_DOC_EDUCATION_TAB_SECTIONS = [
  'academic-Background',
  'Additional_prof_dev',
  'employment_prof_dev',
  'programs-upload'
] as const;

/** Experience tab `profile_tab_section` values that store grid-string category/type. */
export const PROFILE_DOC_EXPERIENCE_TAB_SECTIONS = [
  'out-of-state-lic_notes',
  'teaching-experience',
  'employment-support',
  'business-industry-support',
  'verifiable-teaching-experience'
] as const;

/** Human labels saved on upload map to internal education section keys. */
export const PROFILE_DOC_EDUCATION_SECTION_LABELS: Record<string, string> = {
  'Academic Background': 'academic-Background',
  'District / Charter Employment': 'employment_prof_dev',
  'Additional Professional Development': 'Additional_prof_dev',
  Programs: 'programs-upload'
};

/** Human labels saved on upload map to internal experience section keys. */
export const PROFILE_DOC_EXPERIENCE_SECTION_LABELS: Record<string, string> = {
  'Out of State or Country Licensure': 'out-of-state-lic_notes',
  'Verifiable Teaching Experience': 'verifiable-teaching-experience',
  'Verifiable Business and Industry Work Experience': 'business-industry-support',
  'District / Charter Training Transcript': 'employment-support'
};

/** Category value for Academic Background grid (institution names as type). */
export const ACADEMIC_BACKGROUND_INSTITUTION_CATEGORY_VALUE = 'Academic Background';

export const PROFILE_DOC_COMPLIANCE_HISTORY_SECTION = 'History Disclosure';
export const PROFILE_DOC_OOS_CATEGORY_KEY_LEGACY = '10';

/** Resolve `profile_tab_section` from CMS lookup category metadata (`optionaldata.section_name`). */
export function resolveProfileTabSectionFromLookupCategory(
  category: { optionaldata?: Record<string, unknown> } | null | undefined,
  fallback = 'activities_documents'
): string {
  const raw = category?.optionaldata?.['section_name'] ?? category?.optionaldata?.['sectionName'];
  const section = String(raw ?? '').trim();
  return section || fallback;
}

export interface ProfileDocumentLabelContext {
  categories: SupportingDocumentGridOption[];
  typesByCategory: Record<string, SupportingDocumentGridOption[]>;
  nevadaCategoryKey?: string;
  oosCategoryKey?: string;
  /** Section-scoped category options (upload dropdown source) for Nevada supporting. */
  nevadaCategoryOptions?: SupportingDocumentGridOption[];
  /** Section-scoped category options (upload dropdown source) for OOS supporting. */
  oosCategoryOptions?: SupportingDocumentGridOption[];
  /** Section-scoped category options for each education upload section. */
  educationSectionCategoryOptions?: SupportingDocumentGridOption[];
}

function profileTabSection(doc: Record<string, unknown>): string {
  return String(doc['profile_tab_section'] ?? doc['_raw']?.['profile_tab_section'] ?? '').trim();
}

/** Normalize education profile_tab_section (human label or internal key). */
export function normalizeEducationProfileTabSection(section: string): string | null {
  const s = String(section ?? '').trim();
  if (!s) return null;
  if ((PROFILE_DOC_EDUCATION_TAB_SECTIONS as readonly string[]).includes(s)) {
    return s;
  }
  return PROFILE_DOC_EDUCATION_SECTION_LABELS[s] ?? null;
}

function storedCategoryKey(doc: Record<string, unknown>): string {
  const raw = doc['_raw'] as Record<string, unknown> | undefined;
  return String(
    raw?.['cd_document_category'] ??
      raw?.['document_category'] ??
      raw?.['category'] ??
      doc['cd_document_category'] ??
      doc['category'] ??
      ''
  ).trim();
}

function storedTypeKey(doc: Record<string, unknown>): string {
  const raw = doc['_raw'] as Record<string, unknown> | undefined;
  return String(
    raw?.['cd_document_type'] ??
      raw?.['document_type'] ??
      raw?.['type'] ??
      doc['cd_document_type'] ??
      doc['type'] ??
      ''
  ).trim();
}

function isEducationOrExperienceSection(section: string): boolean {
  return (
    (PROFILE_DOC_EDUCATION_TAB_SECTIONS as readonly string[]).includes(section) ||
    (PROFILE_DOC_EXPERIENCE_TAB_SECTIONS as readonly string[]).includes(section) ||
    normalizeEducationProfileTabSection(section) != null ||
    Object.keys(PROFILE_DOC_EDUCATION_SECTION_LABELS).includes(section)
  );
}

function isAcademicBackgroundEducationSection(section: string): boolean {
  const normalized = normalizeEducationProfileTabSection(section);
  return normalized === 'academic-Background' || section === 'Academic Background';
}

/**
 * Category "Academic Background" (lookup key 19): type is institution name from degrees grid.
 * Other Academic Background section categories (e.g. HSD key 10) use CMS child types.
 */
export function isAcademicBackgroundInstitutionCategory(
  catKey: string,
  ctx: ProfileDocumentLabelContext
): boolean {
  const k = String(catKey ?? '').trim();
  if (!k) return false;
  const fromAll = ctx.categories.find((c) => c.key === k);
  if (fromAll?.value?.trim() === ACADEMIC_BACKGROUND_INSTITUTION_CATEGORY_VALUE) {
    return true;
  }
  return k === 'Academic Background' || k === '19';
}

function normalizeProfileSectionValue(profileSection: string): string {
  return String(profileSection ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function isNevadaSupportingSection(profileSection: string): boolean {
  const p = normalizeProfileSectionValue(profileSection);
  return (
    p === 'nevada praxis testing' ||
    p === 'nevada testing' ||
    p.includes('nevada praxis') ||
    (p.includes('nevada') && p.includes('testing')) ||
    p.includes('activities_nevada_supporting') ||
    p.includes('nevada_supporting_documents')
  );
}

export function isOosSupportingSection(profileSection: string): boolean {
  const p = normalizeProfileSectionValue(profileSection);
  // Licensure is a separate experience section — not OOS testing
  if (p === 'out of state or country licensure') return false;
  return (

    p === 'other testing' ||
    p === 'out of state or country licensure' ||
    p.includes('out_of_state') ||
    p.includes('out-of-state') ||
    p.includes('activities_oos_supporting') ||
    p.includes('oos_supporting_documents') ||
    p.includes('oos')
  );
}

function isOosCategoryKey(catKey: string, ctx: ProfileDocumentLabelContext, profileSection: string): boolean {
  const k = String(catKey ?? '').trim();
  if (!k) return false;
  // HSD / credential categories under Academic Background also use key 10 — not OOS.
  if (isAcademicBackgroundEducationSection(profileSection) && !isAcademicBackgroundInstitutionCategory(k, ctx)) {
    return false;
  }
  return (
    (!!ctx.oosCategoryKey && k === ctx.oosCategoryKey) ||
    k === PROFILE_DOC_OOS_CATEGORY_KEY_LEGACY
  );
}

function isNevadaCategoryKey(catKey: string, ctx: ProfileDocumentLabelContext): boolean {
  const k = String(catKey ?? '').trim();
  return !!ctx.nevadaCategoryKey && k === ctx.nevadaCategoryKey;
}

function isNevadaOosSupportingDocument(
  doc: Record<string, unknown>,
  ctx: ProfileDocumentLabelContext
): boolean {
  const section = profileTabSection(doc);
  if (isNevadaSupportingSection(section) || isOosSupportingSection(section)) {
    return true;
  }
  const catKey = storedCategoryKey(doc);
  return isNevadaCategoryKey(catKey, ctx) || isOosCategoryKey(catKey, ctx, section);
}

/** True when category is a user-chosen grid string (Education/Experience only). */
export function usesGridStringDocumentCategory(
  doc: Record<string, unknown>,
  ctx: ProfileDocumentLabelContext
): boolean {
  const section = profileTabSection(doc);
  if (isAcademicBackgroundEducationSection(section)) {
    return false;
  }
  // employment_prof_dev and Additional_prof_dev store a System A lookup key as category
  const normalized = normalizeEducationProfileTabSection(section);
  if (normalized === 'employment_prof_dev' || normalized === 'Additional_prof_dev') {
    return false;
  }
  return isEducationOrExperienceSection(section);
}

/** True when type is a user-chosen grid string (Education/Experience + Nevada/OOS test names). */
export function usesGridStringDocumentType(
  doc: Record<string, unknown>,
  ctx: ProfileDocumentLabelContext
): boolean {
  const section = profileTabSection(doc);
  if (isAcademicBackgroundEducationSection(section)) {
    return isAcademicBackgroundInstitutionCategory(storedCategoryKey(doc), ctx);
  }
  if (isEducationOrExperienceSection(section)) {
    return true;
  }
  return isNevadaOosSupportingDocument(doc, ctx);
}

/**
 * True when both category and type are grid strings (Education/Experience only).
 * @deprecated Prefer usesGridStringDocumentCategory / usesGridStringDocumentType.
 */
export function usesGridStringDocumentLabels(
  doc: Record<string, unknown>,
  ctx: ProfileDocumentLabelContext
): boolean {
  return usesGridStringDocumentCategory(doc, ctx) && usesGridStringDocumentType(doc, ctx);
}

function resolveCategoryFromSystemA(
  stored: string,
  ctx: ProfileDocumentLabelContext,
  profileSection = '',
): string {
  const fromAll = ctx.categories.find((c) => c.key === stored);
  if (fromAll?.value) {
    return fromAll.value;
  }
  // Try education section-scoped options (same pattern as Nevada/OOS in activities tab)
  if (ctx.educationSectionCategoryOptions?.length) {
    const fromEdu = ctx.educationSectionCategoryOptions.find((c) => c.key === stored);
    if (fromEdu?.value) {
      return fromEdu.value;
    }
  }
  if (isNevadaCategoryKey(stored, ctx)) {
    const fromNv = ctx.nevadaCategoryOptions?.find((c) => c.key === stored);
    if (fromNv?.value) {
      return fromNv.value;
    }
  }
  if (isOosCategoryKey(stored, ctx, profileSection)) {
    const fromOos = ctx.oosCategoryOptions?.find((c) => c.key === stored);
    if (fromOos?.value) {
      return fromOos.value;
    }
  }
  return fromAll?.value || stored;
}

/** Display category: grid strings as-is; lookup-backed rows decode via System A keys only. */
export function resolveProfileDocumentCategoryLabel(
  doc: Record<string, unknown>,
  ctx: ProfileDocumentLabelContext
): string {
  const stored = storedCategoryKey(doc);
  if (!stored) return '';
  if (usesGridStringDocumentCategory(doc, ctx)) {
    return stored;
  }
 return resolveCategoryFromSystemA(stored, ctx, profileTabSection(doc));
}

/** Display type: grid strings as-is; lookup-backed rows decode via System A within category scope.
 * Nevada/OOS legacy Communication uploads may store a lookup key (e.g. `298`) — resolve via typesByCategory when available.
 */
export function resolveProfileDocumentTypeLabel(
  doc: Record<string, unknown>,
  ctx: ProfileDocumentLabelContext
): string {
  const stored = storedTypeKey(doc);
  if (!stored) return '';
  const catKey = storedCategoryKey(doc);
  const types = catKey ? ctx.typesByCategory[catKey] : undefined;
  if (usesGridStringDocumentType(doc, ctx)) {
    const fromLookup = types?.find((t) => t.key === stored);
    if (fromLookup?.value) {
      return fromLookup.value;
    }
    return stored;
  }
  const fromSystemA = types?.find((t) => t.key === stored);
  return fromSystemA?.value || stored;
}

/** Unique System A category keys from documents that need type preload (excludes grid-string type rows). */
export function uniqueSystemACategoryKeysFromDocuments(
  docs: Record<string, unknown>[],
  ctx: ProfileDocumentLabelContext
): string[] {
  const keys = new Set<string>();
  for (const doc of docs) {
    if (usesGridStringDocumentType(doc, ctx)) continue;
    const catKey = storedCategoryKey(doc);
    if (catKey) keys.add(catKey);
  }
  return [...keys];
}
